<template>
  <div class="g703-mapper-container">
    <h3>G703 Configuration</h3>
    
    <div class="mapping-info">
      <p>Configure how to extract and categorize data from your G703 documents. This template will be applied to all G703 files in your project.</p>
    </div>
    
    <div class="sample-file-info">
      <p><strong>Sample File:</strong> {{ sampleFileName }}</p>
      <p><strong>Total G703 Files:</strong> {{ g703FileCount }}</p>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-section">
      <div class="loading-spinner">
        <div class="spinner"></div>
        <p>Loading G703 data via OCR...</p>
      </div>
    </div>

    <!-- G703 Configuration Flow -->
    <div v-if="!isLoading && g703Data" class="g703-config-section">
      <!-- Current Step Display -->
      <div class="current-step">
        <h4>{{ currentStepTitle }}</h4>
        <p>{{ currentStepDescription }}</p>
      </div>

      <!-- Step 1: G703 Column Mapping (like individual uploads) -->
      <div v-if="currentStep === 'column_mapping'" class="g703-column-mapping-step">
        <div class="mapping-header">
          <h4>G703 Column Mapping</h4>
          <p>First, map each column to the correct G703 field. Click on the columns below to assign them:</p>
          
          <!-- EXACT copy of mapping buttons from DrawTemplateSetup.vue -->
          <div class="g703-mapping-buttons-grid">
            <div class="mapping-section">
              <h6>Basic Information</h6>
              <button 
                :class="{ active: currentMappingField === 'trade_code_column' }"
                @click="currentMappingField = 'trade_code_column'"
                class="mapping-btn trade-code-btn">
                🏷️ Trade Code
              </button>
              <button 
                :class="{ active: currentMappingField === 'description_column' }"
                @click="currentMappingField = 'description_column'"
                class="mapping-btn description-btn">
                📝 Description of Work
              </button>
              <button 
                :class="{ active: currentMappingField === 'contractor_names_column' }"
                @click="currentMappingField = 'contractor_names_column'"
                class="mapping-btn contractor-btn">
                👷 Names (Contractor/Sub)
              </button>
            </div>
            
            <div class="mapping-section">
              <h6>Budget Values</h6>
              <button 
                :class="{ active: currentMappingField === 'scheduled_value_column' }"
                @click="currentMappingField = 'scheduled_value_column'"
                class="mapping-btn scheduled-value-btn">
                💰 Scheduled Value
              </button>
              <button 
                :class="{ active: currentMappingField === 'owner_previous_approved_column' }"
                @click="currentMappingField = 'owner_previous_approved_column'"
                class="mapping-btn owner-previous-btn">
                ✅ Owner Previous Approved
              </button>
              <button 
                :class="{ active: currentMappingField === 'adjusted_schedule_values_column' }"
                @click="currentMappingField = 'adjusted_schedule_values_column'"
                class="mapping-btn adjusted-schedule-btn">
                📊 Adjusted Schedule Values
              </button>
            </div>
            
            <div class="mapping-section">
              <h6>Work Completed</h6>
              <button 
                :class="{ active: currentMappingField === 'work_completed_previous_column' }"
                @click="currentMappingField = 'work_completed_previous_column'"
                class="mapping-btn previous-work-btn">
                ⏮️ Work Completed Previous
              </button>
              <button 
                :class="{ active: currentMappingField === 'work_completed_this_period_column' }"
                @click="currentMappingField = 'work_completed_this_period_column'"
                class="mapping-btn current-work-btn">
                🔄 Work Completed This Period
              </button>
              <button 
                :class="{ active: currentMappingField === 'materials_stored_column' }"
                @click="currentMappingField = 'materials_stored_column'"
                class="mapping-btn materials-btn">
                📦 Materials Presently Stored
              </button>
              <button 
                :class="{ active: currentMappingField === 'total_completed_column' }"
                @click="currentMappingField = 'total_completed_column'"
                class="mapping-btn total-completed-btn">
                📋 Total Completed and Stored
              </button>
              <button 
                :class="{ active: currentMappingField === 'percent_complete_column' }"
                @click="currentMappingField = 'percent_complete_column'"
                class="mapping-btn percent-complete-btn">
                📈 Percentage Complete
              </button>
              <button 
                :class="{ active: currentMappingField === 'balance_to_finish_column' }"
                @click="currentMappingField = 'balance_to_finish_column'"
                class="mapping-btn balance-finish-btn">
                🎯 Balance to Finish
              </button>
            </div>
            
            <div class="mapping-section">
              <h6>Retainage</h6>
              <button 
                :class="{ active: currentMappingField === 'retainage_percent_column' }"
                @click="currentMappingField = 'retainage_percent_column'"
                class="mapping-btn retainage-percent-btn">
                🔒 Retainage %
              </button>
              <button 
                :class="{ active: currentMappingField === 'retainage_this_period_column' }"
                @click="currentMappingField = 'retainage_this_period_column'"
                class="mapping-btn retainage-period-btn">
                🔒 Retainage This Period
              </button>
              <button 
                :class="{ active: currentMappingField === 'retainage_total_column' }"
                @click="currentMappingField = 'retainage_total_column'"
                class="mapping-btn retainage-total-btn">
                🔒 Retainage Total
              </button>
            </div>
          </div>
          
          <p class="mapping-instruction">
            <strong>Currently mapping:</strong> {{ g703MappingLabels[currentMappingField] || 'Description of Work' }}
            <span v-if="g703ColumnMapping[currentMappingField] !== null" class="current-mapping">
              (Mapped to Column {{ g703ColumnMapping[currentMappingField] + 1 }})
            </span>
          </p>
        </div>
        
        <!-- G703 Table Preview - EXACT copy from DrawTemplateSetup.vue -->
        <div class="g703-table-preview">
          <h5>Click on the Header Row:</h5>
          <p class="table-info">Showing {{ sampleG703Rows?.[0]?.length || 0 }} extracted data points from your G703 document</p>
          <div class="raw-table-container">
            <table class="raw-g703-table mapping-mode">
              <tbody>
                <tr v-for="(row, rowIndex) in sampleG703Rows" :key="rowIndex"
                    :class="{ 'selected-header-row': g703HeaderRow === rowIndex }"
                    @click="selectG703HeaderRow(rowIndex)">
                  <td class="row-number">{{ rowIndex + 1 }}</td>
                  <td v-for="(cell, cellIndex) in (Array.isArray(row) ? row : [row])" :key="cellIndex" 
                      class="table-cell clickable-cell"
                      :class="getG703ColumnHighlight(cellIndex, currentMappingField)"
                      @click.stop="selectG703Column(cellIndex, currentMappingField)">
                    {{ getCellDisplayText(cell) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="extraction-explanation">
            <h6>📋 What happened during extraction?</h6>
            <p><strong>OCR Result:</strong> Your G703 PDF was processed and extracted as a single row with {{ sampleG703Rows?.[0]?.length || 0 }} data points.</p>
            <p><strong>Why this happens:</strong> G703 forms have complex layouts that OCR sometimes reads as continuous text rather than structured tables.</p>
            <p><strong>Next step:</strong> Click on row 1 above to select it as your header row, then click individual cells to map them to G703 fields.</p>
            <p class="tip">💡 <strong>Tip:</strong> The extracted data contains all your G703 form fields - we'll help you map them to the correct columns.</p>
          </div>
        </div>
        
        <!-- EXACT mapping summary from DrawTemplateSetup.vue -->
        <div class="mapping-summary">
          <h5>Column Mappings:</h5>
          <div class="mapping-list">
            <div v-for="(label, mappingKey) in g703MappingLabels" :key="mappingKey" class="mapping-summary-item">
              <span class="field-label">{{ label }}:</span>
              <span class="mapped-column" :class="{ mapped: g703ColumnMapping[mappingKey] !== null }">
                {{ g703ColumnMapping[mappingKey] !== null ? `Column ${g703ColumnMapping[mappingKey] + 1}` : 'Not mapped' }}
              </span>
            </div>
          </div>
          
          <!-- Mapping Progress Section -->
          <div class="mapping-progress-section">
            <p><strong>Mapping Progress:</strong> {{ mappedColumnsCount }}/17 columns mapped ({{ essentialMappedCount }}/4 essential)</p>
            <div class="essential-status">
              <p><strong>Essential mappings:</strong></p>
              <ul>
                <li :class="{ mapped: g703ColumnMapping.description_column !== null }">
                  ✓ Description of Work {{ g703ColumnMapping.description_column !== null ? `(Col ${g703ColumnMapping.description_column + 1})` : '' }}
                </li>
                <li :class="{ mapped: g703ColumnMapping.scheduled_value_column !== null }">
                  ✓ Scheduled Value {{ g703ColumnMapping.scheduled_value_column !== null ? `(Col ${g703ColumnMapping.scheduled_value_column + 1})` : '' }}
                </li>
                <li :class="{ mapped: g703ColumnMapping.work_completed_this_period_column !== null }">
                  ✓ Work Completed This Period {{ g703ColumnMapping.work_completed_this_period_column !== null ? `(Col ${g703ColumnMapping.work_completed_this_period_column + 1})` : '' }}
                </li>
                <li :class="{ mapped: g703ColumnMapping.total_completed_column !== null }">
                  ✓ Total Completed {{ g703ColumnMapping.total_completed_column !== null ? `(Col ${g703ColumnMapping.total_completed_column + 1})` : '' }}
                </li>
              </ul>
            </div>
            <div class="next-step-actions">
              <button @click="proceedToDataReview" :disabled="!hasEssentialMappings || isLoading" class="btn-primary">
                <span v-if="isLoading">
                  🔄 Applying Template & Re-processing G703...
                </span>
                <span v-else>
                  Apply Template & Re-scan G703
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Step 2: Data Review & Correction -->
      <div v-if="currentStep === 'data_review'" class="g703-data-review-step">
        <h4>Review Extracted G703 Data</h4>
        <p>Review the data extracted using your column mappings. Make any necessary corrections:</p>
        
        <div class="extracted-data-table">
          <table class="review-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Scheduled Value</th>
                <th>Previous Work</th>
                <th>Current Work</th>
                <th>Contractor</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in processedG703Data" :key="index">
                <td>{{ item.description }}</td>
                <td>{{ formatAmount(item.original_budget) }}</td>
                <td>{{ formatAmount(item.previous_amount) }}</td>
                <td>{{ formatAmount(item.current_amount) }}</td>
                <td>{{ item.contractor }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div class="review-actions">
          <button @click="proceedToDDAMapping" class="btn-primary">
            Data Looks Good - Continue to DDA Mapping
          </button>
          <button @click="goBackToColumnMapping" class="btn-secondary">
            Back to Column Mapping
          </button>
        </div>
      </div>

            <!-- Step 3: DDA Term Mapping Step -->
      <div v-if="currentStep === 'dda_mapping'" class="dda-term-mapping-step">
        <div class="mapping-interface">
          <div class="mapping-controls">
            <button @click="autoAssignTerms" class="btn-secondary">🤖 Auto-Assign DDA Terms</button>
            <button @click="testMapping" class="btn-secondary" :disabled="testLoading">
              {{ testLoading ? 'Testing...' : '🧪 Test Mapping' }}
            </button>
          </div>

          <div class="dda-mapping-table-container">
            <table class="dda-mapping-table">
              <thead>
                <tr>
                  <th class="code-header">Code</th>
                  <th class="description-header">Description of Work</th>
                  <th class="contractor-header">Contractor</th>
                  <th class="dda-term-header">DDA Term</th>
                  <th class="amount-header">Scheduled Value</th>
                  <th class="amount-header">This Period</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in classificationItems" :key="index"
                    :class="{ 'selected-row': selectedG703Item === index }"
                    @click="selectG703Item(index)">
                  <td class="code-cell">{{ item.code }}</td>
                  <td class="description-cell">{{ item.description }}</td>
                  <td class="contractor-cell">{{ item.contractor }}</td>
                  <td class="dda-term-cell">
                    <div class="dda-term-wrapper">
                      <span v-if="item.auto_matched" class="auto-match-badge">AUTO</span>
                      <div class="dda-autocomplete" :class="{ 'auto-matched': item.auto_matched }">
                        <input 
                          type="text" 
                          v-model="item.searchTerm"
                          :placeholder="item.dda_term_id ? getSelectedTermName(item.dda_term_id) : 'Search DDA Terms...'"
                          @input="searchDDATerms(item, $event.target.value)"
                          @focus="handleFocus(item)"
                          @blur="handleBlur(item)"
                          class="dda-search-input"
                          :class="{ 'has-selection': item.dda_term_id, 'auto-matched': item.auto_matched }"
                        />
                        <div v-if="item.showDropdown && item.searchResults" class="dda-dropdown">
                          <div v-if="item.searchResults.length === 0" class="no-results">
                            No matching DDA terms found
                          </div>
                          <div 
                            v-for="term in item.searchResults.slice(0, 10)" 
                            :key="term.id"
                            class="dda-option"
                            @mousedown="selectDDATerm(item, term)"
                          >
                            <div class="term-info">
                              <span class="term-name">{{ term.term }}</span>
                              <span class="term-category">{{ term.category }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td class="amount-cell">{{ formatAmount(item.scheduled_value) }}</td>
                  <td class="amount-cell">{{ formatAmount(item.work_completed_this_period) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pattern Mapping Section -->
          <div class="pattern-mapping-section">
            <h4>Description Patterns</h4>
            <p>Create patterns to automatically assign DDA terms to similar descriptions:</p>
            
            <div class="pattern-list">
              <div v-for="(pattern, index) in descriptionPatterns" :key="index" class="pattern-item">
                <input 
                  type="text" 
                  v-model="pattern.pattern" 
                  placeholder="e.g., 'concrete', 'steel', 'bim'"
                  class="pattern-input"
                />
                <span class="pattern-arrow">→</span>
                <select v-model="pattern.dda_term_id" class="pattern-select">
                  <option value="">Select DDA Term</option>
                  <option v-for="term in ddaTerms" :key="term.id" :value="term.id">
                    {{ term.term }}
                  </option>
                </select>
                <button @click="removePattern(index)" class="btn-remove">×</button>
              </div>
            </div>
            
            <button @click="addPattern" class="btn-secondary">+ Add Pattern</button>
          </div>

          <!-- Test Results -->
          <div v-if="testResults" class="test-results">
            <h4>Mapping Test Results</h4>
            <div class="test-stats">
              <div class="stat-item">
                <strong>{{ testResults.mapping_results.dda_term_assignments }}</strong> exact matches
              </div>
              <div class="stat-item">
                <strong>{{ testResults.mapping_results.pattern_matches }}</strong> pattern matches
              </div>
              <div class="stat-item">
                <strong>{{ testResults.mapping_results.contractor_assignments }}</strong> contractor matches
              </div>
              <div class="stat-item">
                <strong>{{ testResults.mapping_results.unmapped_items }}</strong> unmapped items
              </div>
            </div>
            
            <div class="assignment-rate">
              Assignment Rate: <strong>{{ assignmentRate }}%</strong>
            </div>
          </div>
        </div>
      </div>

      <!-- Navigation -->
      <div class="step-navigation">
        <button @click="saveConfiguration" class="btn-primary" :disabled="!canSave">
          Save G703 Template & Continue
        </button>
      </div>
    </div>

    <!-- Fallback for when G703 data is not available -->
    <div v-if="!isLoading && !g703Data" class="g703-preview-fallback">
      <h4>G703 Preview & Mapping</h4>
      <div class="placeholder-message">
        <p>⚠️ No G703 files found or unable to load G703 preview data</p>
        <p>Skipping G703 configuration...</p>
      </div>
      
      <div class="actions">
        <button @click="skipG703Configuration" class="btn-primary">Skip G703 & Continue</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'

const props = defineProps({
  projectImport: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['complete'])

// Component state
const isLoading = ref(true)
const g703Data = ref(null)
const selectedG703Item = ref(null)
const classificationItems = ref([])
const descriptionPatterns = ref([])
const ddaTerms = ref([])
const testResults = ref(null)
const testLoading = ref(false)

// G703 Template Flow State
const currentStep = ref('column_mapping') // Start with column mapping like individual uploads
const currentMappingField = ref('description_column')
const g703HeaderRow = ref(0) // Selected header row (like individual upload)
const g703ColumnMapping = ref({
  trade_code_column: null,
  description_column: null,
  contractor_names_column: null,
  scheduled_value_column: null,
  owner_previous_approved_column: null,
  owner_approved_completed_column: null,
  adjusted_schedule_values_column: null,
  subcontractor_completed_column: null,
  work_completed_previous_column: null,
  work_completed_this_period_column: null,
  materials_stored_column: null,
  total_completed_column: null,
  percent_complete_column: null,
  balance_to_finish_column: null,
  retainage_percent_column: null,
  retainage_this_period_column: null,
  retainage_total_column: null
})
const processedG703Data = ref([])
const sampleG703Rows = ref([])

// G703 mapping field labels - EXACT match to DrawTemplateSetup.vue
const g703MappingLabels = {
  'trade_code_column': 'Trade Code',
  'description_column': 'Description of Work',
  'contractor_names_column': 'Names (Contractor/Sub)',
  'scheduled_value_column': 'Scheduled Value',
  'budget_transfer_column': 'Budget Transfer',
  'owner_previous_approved_column': 'Owner Previous Approved',
  'owner_approved_completed_column': 'Owner Approved Completed',
  'adjusted_schedule_values_column': 'Adjusted Schedule Values',
  'subcontractor_completed_column': 'Subcontractor Completed',
  'work_completed_previous_column': 'Work Completed Previous',
  'work_completed_this_period_column': 'Work Completed This Period',
  'materials_stored_column': 'Materials Presently Stored',
  'total_completed_column': 'Total Completed and Stored',
  'percent_complete_column': 'Percentage Complete',
  'balance_to_finish_column': 'Balance to Finish',
  'retainage_percent_column': 'Retainage %',
  'retainage_this_period_column': 'Retainage This Period',
  'retainage_total_column': 'Retainage Total'
}

// Computed properties
const sampleFileName = computed(() => {
  const draws = props.projectImport.scan_results?.draws || []
  const drawWithG703 = draws.find(d => d.files?.some(f => f.type === 'g703'))
  const g703File = drawWithG703?.files?.find(f => f.type === 'g703')
  return g703File?.filename || 'No G703 files found'
})

const g703FileCount = computed(() => {
  const draws = props.projectImport.scan_results?.draws || []
  return draws.filter(d => d.files?.some(f => f.type === 'g703')).length
})

const currentStepTitle = computed(() => {
  switch (currentStep.value) {
    case 'column_mapping':
      return 'Step 1: Map G703 Columns'
    case 'data_review':
      return 'Step 2: Review Extracted Data'
    case 'dda_mapping':
      return 'Step 3: Map DDA Terms to G703 Line Items'
    default:
      return 'G703 Template Configuration'
  }
})

const currentStepDescription = computed(() => {
  switch (currentStep.value) {
    case 'column_mapping':
      return 'First, map the columns in your G703 to the correct fields. This ensures accurate data extraction.'
    case 'data_review':
      return 'Review the extracted data to make sure the column mapping worked correctly.'
    case 'dda_mapping':
      return 'Assign DDA terms to each line item and create patterns for automatic assignment.'
    default:
      return 'Configure your G703 template'
  }
})

// Column mapping computed properties
const mappedColumnsCount = computed(() => {
  return Object.values(g703ColumnMapping.value).filter(val => val !== null).length
})

const hasEssentialMappings = computed(() => {
  return g703ColumnMapping.value.description_column !== null &&
         g703ColumnMapping.value.scheduled_value_column !== null &&
         (g703ColumnMapping.value.work_completed_this_period_column !== null ||
          g703ColumnMapping.value.total_completed_column !== null)
})

const essentialMappedCount = computed(() => {
  let count = 0
  if (g703ColumnMapping.value.description_column !== null) count++
  if (g703ColumnMapping.value.scheduled_value_column !== null) count++
  if (g703ColumnMapping.value.work_completed_this_period_column !== null) count++
  if (g703ColumnMapping.value.total_completed_column !== null) count++
  return count
})

const canSave = computed(() => {
  return classificationItems.value.some(item => item.dda_term_id) || 
         descriptionPatterns.value.some(p => p.pattern && p.dda_term_id)
})

const assignmentRate = computed(() => {
  if (!testResults.value) return 0
  const total = testResults.value.total_rows
  const unmapped = testResults.value.mapping_results.unmapped_items
  return Math.round(((total - unmapped) / total) * 100)
})

// Methods
const loadG703Preview = async () => {
  try {
    const response = await fetch(`/api/v1/project_imports/${props.projectImport.id}/g703_preview`)
    const data = await response.json()
    
    console.log('G703 Preview Response:', data)
    
    if (data.success) {
      // Backend returns raw_table_data for template creation (like individual uploads)
      const rawTableData = data.preview_data?.raw_table_data || []
      
      if (rawTableData.length > 0) {
        console.log(`✅ G703 raw table data loaded: ${rawTableData.length} table rows`)
        console.log('Sample raw data:', rawTableData[0])
        
        // Store the raw table data for column mapping (like DrawTemplateSetup.vue)
        sampleG703Rows.value = rawTableData
        
        // Store the response data for template creation
        g703Data.value = {
          ...data,
          raw_table_data: rawTableData,
          extraction_method: data.preview_data?.extraction_method || 'unknown'
        }
        
        // For G703 template creation, we don't need line_items yet
        // We need to create the template first, then process the line items
        console.log(`✅ Ready for G703 template creation with ${rawTableData.length} raw rows`)
        console.log(`🎯 Extraction method: ${data.preview_data?.extraction_method}`)
        
      } else {
        console.warn('No raw table data available in G703 preview response')
      }
    } else {
      console.warn('G703 preview request failed:', data.error || 'Unknown error')
    }
  } catch (error) {
    console.error('Error loading G703 preview:', error)
  } finally {
    isLoading.value = false
  }
}

const loadDDATerms = async () => {
  try {
    const response = await fetch('/api/v1/dda_terms')
    const data = await response.json()
    ddaTerms.value = data.dda_terms || []
  } catch (error) {
    console.error('Error loading DDA terms:', error)
  }
}

const selectG703Item = (index) => {
  selectedG703Item.value = index
}

const searchDDATerms = async (item, searchTerm) => {
  if (!searchTerm || searchTerm.length < 2) {
    item.searchResults = []
    item.showDropdown = false
    return
  }

  try {
    // Use the same backend API as Excel for intelligent search
    const response = await fetch('/api/v1/dda_terms/search', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      body: null,
      // Use URLSearchParams for GET request
    })
    
    const url = new URL('/api/v1/dda_terms/search', window.location.origin)
    url.searchParams.append('q', searchTerm)
    url.searchParams.append('limit', '20')
    
    const searchResponse = await fetch(url)
    const searchData = await searchResponse.json()
    
    item.searchResults = searchData || []
    item.showDropdown = true
    console.log(`G703 search for "${searchTerm}" returned ${item.searchResults.length} results`)
  } catch (error) {
    console.error('Error searching DDA terms via backend:', error)
    
    // Fallback to client-side filtering
    const filtered = ddaTerms.value.filter(term => 
      term.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
      term.category?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    
    item.searchResults = filtered
    item.showDropdown = true
  }
}

const handleFocus = (item) => {
  if (item.searchResults?.length > 0) {
    item.showDropdown = true
  }
}

const handleBlur = (item) => {
  setTimeout(() => {
    item.showDropdown = false
  }, 200)
}

const selectDDATerm = (item, term) => {
  console.log('Selecting G703 DDA term:', term);
  item.dda_term_id = term.id
  item.searchTerm = term.term
  item.showDropdown = false
  item.searchResults = []
  item.auto_matched = false
  
  // Store the full term info for display (same as Excel)
  item.selectedTerm = term
}

const getSelectedTermName = (termId) => {
  const term = ddaTerms.value.find(t => t.id === termId)
  return term?.term || ''
}

// Prepare classification items from G703 data (same logic as Excel)
const prepareClassificationItems = () => {
  if (!processedG703Data.value || processedG703Data.value.length === 0) return
  
  classificationItems.value = []
  
  processedG703Data.value.forEach((item, index) => {
    const classItem = {
      code: item.trade_code || '',
      description: item.description || '',
      contractor: item.contractor_name || '',
      scheduled_value: item.scheduled_value || 0,
      this_period: item.work_completed_this_period || 0,
      dda_term_id: null,
      searchTerm: '',
      showDropdown: false,
      searchResults: [],
      auto_matched: false,
      selectedTerm: null
    }
    
    classificationItems.value.push(classItem)
  })
  
  console.log(`Prepared ${classificationItems.value.length} G703 items for DDA term classification`)
}

// Auto-match DDA terms using backend ML API (same as Excel)
const autoMatchDDATerms = async () => {
  if (classificationItems.value.length === 0) return
  
  console.log('Starting G703 auto-match for DDA terms...')
  
  try {
    // Extract descriptions for classification
    const descriptions = classificationItems.value.map(item => item.description)
    
    // Call the same classify endpoint as Excel using axios (same as Excel)
    const response = await axios.post('/api/v1/dda_terms/classify', {
      descriptions: descriptions
    })
    
    const data = response.data
    
    console.log('🔍 Classification response:', data);
    
    if (data && data.classifications) {
      console.log(`📊 Processing ${data.classifications.length} classifications for ${classificationItems.value.length} items`);
      
      // Apply the classifications
      data.classifications.forEach((classification, index) => {
        if (index < classificationItems.value.length) {
          console.log(`🎯 Item ${index}: "${classificationItems.value[index].description}" -> DDA Term ID: ${classification.dda_term_id}, Confidence: ${classification.confidence}`);
          
          if (classification.dda_term_id && classification.confidence > 0.3) {
            classificationItems.value[index].dda_term_id = classification.dda_term_id
            classificationItems.value[index].searchTerm = classification.dda_term_name || classification.term
            classificationItems.value[index].auto_matched = true
            classificationItems.value[index].selectedTerm = {
              id: classification.dda_term_id,
              term: classification.dda_term_name || classification.term
            }
            console.log(`✅ Auto-matched "${classificationItems.value[index].description}" to "${classification.dda_term_name || classification.term}"`);
          } else {
            console.log(`❌ No match for "${classificationItems.value[index].description}" (confidence: ${classification.confidence})`);
          }
        }
      })
      
      const matchedCount = classificationItems.value.filter(item => item.auto_matched).length
      console.log(`🎉 G703 auto-matched ${matchedCount} out of ${classificationItems.value.length} items`);
      
      // Force Vue reactivity update
      classificationItems.value = [...classificationItems.value];
    } else {
      console.warn('⚠️ No classifications returned from backend');
    }
  } catch (error) {
    console.error('Error auto-matching G703 DDA terms:', error)
    // Continue without auto-matching
  }
}

const autoAssignTerms = () => {
  classificationItems.value.forEach(item => {
    if (item.dda_term_id) return // Skip already assigned items
    
    const description = item.description?.toLowerCase() || ''
    
    // Simple auto-assignment logic
    const autoMappings = {
      'concrete': ddaTerms.value.find(t => t.term.toLowerCase().includes('concrete')),
      'steel': ddaTerms.value.find(t => t.term.toLowerCase().includes('steel')),
      'bim': ddaTerms.value.find(t => t.term.toLowerCase().includes('consultant')),
      'coordination': ddaTerms.value.find(t => t.term.toLowerCase().includes('consultant')),
      'surveying': ddaTerms.value.find(t => t.term.toLowerCase().includes('consultant')),
      'masonry': ddaTerms.value.find(t => t.term.toLowerCase().includes('masonry')),
      'roofing': ddaTerms.value.find(t => t.term.toLowerCase().includes('roofing'))
    }
    
    for (const [keyword, term] of Object.entries(autoMappings)) {
      if (description.includes(keyword) && term) {
        item.dda_term_id = term.id
        item.searchTerm = term.term
        item.auto_matched = true
        break
      }
    }
  })
}

const addPattern = () => {
  descriptionPatterns.value.push({
    pattern: '',
    dda_term_id: null
  })
}

const removePattern = (index) => {
  descriptionPatterns.value.splice(index, 1)
}

const testMapping = async () => {
  testLoading.value = true
  try {
    const templateConfig = buildTemplateConfig()
    
    const response = await fetch(`/api/v1/project_imports/${props.projectImport.id}/test_g703_mapping`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ template_config: templateConfig })
    })

    const data = await response.json()
    
    if (data.success) {
      testResults.value = data
    } else {
      throw new Error(data.error || 'Test failed')
    }
  } catch (error) {
    console.error('Test mapping error:', error)
    alert(`Test failed: ${error.message}`)
  } finally {
    testLoading.value = false
  }
}

const buildTemplateConfig = () => {
  const ddaTermMappings = {}
  const contractorMappings = {}
  
  classificationItems.value.forEach(item => {
    if (item.dda_term_id && item.description) {
      ddaTermMappings[item.description] = item.dda_term_id
    }
    if (item.contractor) {
      contractorMappings[item.contractor] = 1 // Simple contractor ID
    }
  })
  
  const descriptionPatternMappings = {}
  descriptionPatterns.value.forEach(pattern => {
    if (pattern.pattern && pattern.dda_term_id) {
      descriptionPatternMappings[pattern.pattern] = pattern.dda_term_id
    }
  })
  
  return {
    g703_mapping: {
      header_row: 1,
      column_mappings: g703ColumnMapping.value, // Include the column mappings
      dda_term_mappings: ddaTermMappings,
      contractor_mappings: contractorMappings,
      description_patterns: descriptionPatternMappings,
      dollar_figure_categories: {
        scheduled_value: 'Scheduled Value',
        work_completed_this_period: 'Work Completed This Period',
        materials_stored: 'Materials Stored',
        retainage: 'Retainage'
      }
    }
  }
}

const saveConfiguration = async () => {
  try {
    const templateConfig = buildTemplateConfig()
    
    // Get existing Excel config and merge with G703 config
    const existingConfig = props.projectImport.template_config || {}
    const finalConfig = {
      template_config: {
        ...existingConfig,
        ...templateConfig
      }
    }
    
    const response = await fetch(`/api/v1/project_imports/${props.projectImport.id}/configure_templates`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(finalConfig)
    })

    const data = await response.json()
    
    if (data.success) {
      emit('complete', data.template_config)
    } else {
      throw new Error(data.error || 'Failed to save configuration')
    }
  } catch (error) {
    console.error('Save configuration error:', error)
    alert(`Error saving configuration: ${error.message}`)
  }
}

const skipG703Configuration = () => {
  emit('complete', {})
}

const formatAmount = (amount) => {
  if (!amount || amount === 0) return '$0.00'
  return `$${parseFloat(amount).toLocaleString()}`
}

// Updated G703 Template Flow Methods - Use same highlighting as individual upload
const getColumnHighlight = (columnIndex) => {
  return getG703ColumnHighlight(columnIndex, currentMappingField.value)
}

// EXACT methods from DrawTemplateSetup.vue for G703 column mapping
const selectG703HeaderRow = (rowIndex) => {
  g703HeaderRow.value = rowIndex
  console.log(`Selected G703 header row ${rowIndex + 1}:`, sampleG703Rows.value[rowIndex])
  
  // Reset column mappings when header row changes
  g703ColumnMapping.value = {
    trade_code_column: null,
    description_column: null,
    contractor_names_column: null,
    scheduled_value_column: null,
    owner_previous_approved_column: null,
    owner_approved_completed_column: null,
    adjusted_schedule_values_column: null,
    subcontractor_completed_column: null,
    work_completed_previous_column: null,
    work_completed_this_period_column: null,
    materials_stored_column: null,
    total_completed_column: null,
    percent_complete_column: null,
    balance_to_finish_column: null,
    retainage_percent_column: null,
    retainage_this_period_column: null,
    retainage_total_column: null
  }
}

const selectG703Column = (columnIndex, fieldName) => {
  g703ColumnMapping.value[fieldName] = columnIndex
  console.log(`Mapped ${fieldName} to column ${columnIndex}`)
  
  // Don't auto-advance - let user manually select which field to map next
  // This matches the behavior users expect from individual upload
}

const getG703ColumnHighlight = (cellIndex, currentField) => {
  // If this column is mapped to the current field, highlight it with the field color
  if (g703ColumnMapping.value[currentField] === cellIndex) {
    return `highlight-${currentField.replace('_column', '')}`;
  }
  
  // If this column is mapped to any other field, show a subtle mapping
  for (const [fieldKey, mappedCol] of Object.entries(g703ColumnMapping.value)) {
    if (mappedCol === cellIndex && fieldKey !== currentField) {
      return 'highlight-mapped-other';
    }
  }
  
  return '';
}

const mapColumn = (columnIndex) => {
  // Legacy method - redirect to new selectG703Column method
  selectG703Column(columnIndex, currentMappingField.value)
}

const proceedToDataReview = async () => {
  console.log('=== APPLYING COLUMN MAPPING FOR RE-PROCESSING ===');
  
  // Show loading state
  isLoading.value = true;
  
  try {
    console.log('🔄 Applying template and re-processing G703 data...');
    
    const projectImportId = props.projectImport?.id;
    if (!projectImportId) {
      throw new Error('Project import ID not found');
    }
    
    // Send column mappings to backend for template creation and re-processing
    const mappingPayload = {
      template_type: 'g703',
      column_mappings: g703ColumnMapping.value,
      header_row: g703HeaderRow.value || 0,
      template_name: `G703 Template - Project Import ${projectImportId}`
    };
    
    console.log('Sending column mappings to backend:', mappingPayload);
    console.log('Project import ID:', projectImportId);
    
    const response = await fetch(`/api/v1/project_imports/${projectImportId}/apply_g703_template`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(mappingPayload)
    });
    
    const data = await response.json();
    
    if (data.success) {
      console.log('✅ Backend successfully applied template and re-processed G703!');
      
      // Update with properly structured results
      processedG703Data.value = data.structured_data || [];
      
      console.log(`📋 Received ${processedG703Data.value.length} properly structured G703 line items`);
      console.log('Sample structured data:', processedG703Data.value.slice(0, 2));
      
      // Prepare classification items for DDA term mapping (same as Excel)
      console.log('📋 Preparing classification items...');
      prepareClassificationItems();
      
      // Auto-match DDA terms like Excel does
      console.log('🤖 Starting auto-match for DDA terms...');
      await autoMatchDDATerms();
      
      console.log('✅ Auto-matching complete, proceeding to data review');
      
      // Proceed to data review with real structured data
      currentStep.value = 'data_review';
      return;
    } else {
      throw new Error(data.error || 'Failed to apply template');
    }
    
  } catch (error) {
    console.error('❌ Error applying template:', error);
    alert(`Error applying G703 template: ${error.message}`);
  } finally {
    isLoading.value = false;
  }
}

const proceedToDDAMapping = () => {
  // Don't repopulate classificationItems - they're already populated with auto-matching
  // Just move to the DDA mapping step
  console.log(`🎯 Using existing ${classificationItems.value.length} items with auto-matching for DDA mapping`)
  console.log('Sample classification item:', classificationItems.value[0])
  
  currentStep.value = 'dda_mapping'
}

const goBackToColumnMapping = () => {
  currentStep.value = 'column_mapping'
}

const getCellDisplayText = (cell) => {
  // Handle different cell formats from AWS Textract
  if (typeof cell === 'string') {
    // Simple string - truncate if too long
    return cell.length > 50 ? cell.substring(0, 50) + '...' : cell
  } else if (cell && typeof cell === 'object') {
    // AWS Textract cell object - extract text content with more fallbacks
    let text = ''
    
    if (Array.isArray(cell)) {
      // Handle array case
      text = cell.join(' ')
    } else {
      // Handle object case - try multiple properties
      text = cell.text || cell.value || cell.content || cell.data || cell.cellValue || ''
      
      // If still no text, try to stringify and extract meaningful content
      if (!text && cell.toString() !== '[object Object]') {
        text = cell.toString()
      } else if (!text) {
        // Last resort: try to find any string property
        const stringProps = Object.values(cell).filter(v => typeof v === 'string' && v.length > 0)
        text = stringProps[0] || '[No text]'
      }
    }
    
    // Ensure we have a string and truncate if needed
    const finalText = String(text || '')
    return finalText.length > 50 ? finalText.substring(0, 50) + '...' : finalText
  } else {
    // Fallback for null/undefined/other types
    return String(cell || '')
  }
}

// Initialize component
onMounted(() => {
  loadG703Preview()
  loadDDATerms()
  
  // Initialize with some common patterns
  descriptionPatterns.value = [
    { pattern: 'concrete', dda_term_id: null },
    { pattern: 'steel', dda_term_id: null },
    { pattern: 'bim', dda_term_id: null }
  ]
})
</script>

<style scoped>
.g703-mapper-container {
  padding: 20px 0;
}

.mapping-info {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.sample-file-info {
  background: #e3f2fd;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 20px;
}

.loading-section {
  text-align: center;
  padding: 40px 0;
}

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #1890ff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* G703 Column Mapping Styles */
.g703-column-mapping-step {
  margin-bottom: 30px;
}

.g703-mapping-buttons-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin: 20px 0;
}

.mapping-section {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.mapping-section h6 {
  margin: 0 0 12px 0;
  color: #333;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.mapping-section .mapping-btn {
  display: block;
  width: 100%;
  margin-bottom: 8px;
  text-align: left;
}

.mapping-btn {
  padding: 8px 16px;
  border: 2px solid #ddd;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.mapping-btn:hover {
  border-color: #1890ff;
  background: #f0f8ff;
}

.mapping-btn.active {
  border-color: #1890ff;
  background: #1890ff;
  color: white;
}

.mapping-instruction {
  background: #e3f2fd;
  padding: 12px;
  border-radius: 6px;
  margin: 16px 0;
}

.current-mapping {
  color: #2196f3;
  font-weight: 500;
}

.g703-mapping-table {
  width: 100%;
  border-collapse: collapse;
  margin: 20px 0;
}

.g703-mapping-table td {
  border: 1px solid #ddd;
  padding: 8px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.g703-mapping-table .row-number {
  background: #f5f5f5;
  font-weight: bold;
  text-align: center;
  width: 40px;
}

.g703-mapping-table .clickable-cell:hover {
  background: #f0f8ff;
}

.g703-mapping-table .selected-column {
  background: #2196f3 !important;
  color: white;
}

/* EXACT mapping summary styles from DrawTemplateSetup.vue */
.mapping-summary {
  margin-top: 25px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 6px;
  
  h5 {
    color: #333;
    margin-bottom: 15px;
  }
  
  .mapping-list {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 10px;
    margin-bottom: 20px;
    
    .mapping-summary-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 12px;
      background: white;
      border-radius: 4px;
      font-size: 12px;
      
      .field-label {
        font-weight: 600;
        color: #333;
      }
      
      .mapped-column {
        color: #999;
        
        &.mapped {
          color: #4caf50;
          font-weight: 600;
        }
      }
    }
  }
  
  .mapping-progress-section {
    background: #fff;
    padding: 16px;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    margin-top: 15px;
  }
}

/* G703 Table Preview Styles - EXACT copy from DrawTemplateSetup.vue */
.g703-table-preview {
  margin-top: 20px;
  
  h5 {
    margin: 0 0 10px 0;
    color: #333;
    font-size: 16px;
  }
  
  .table-info {
    margin: 0 0 15px 0;
    color: #666;
    font-size: 14px;
  }
  
  .raw-table-container {
    overflow-x: auto;
    max-height: 400px;
    overflow-y: auto;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    margin-bottom: 20px;
  }
  
  .raw-g703-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 10px;
    min-width: 600px;
    
  }
  
  .raw-g703-table.mapping-mode tr {
    cursor: pointer;
    transition: background-color 0.2s;
  }
  
  .raw-g703-table.mapping-mode tr:hover {
    background-color: #f8f9fa;
  }
  
  .raw-g703-table.mapping-mode tr.selected-header-row {
    background-color: #e3f2fd !important;
    border: 2px solid #2196F3;
    font-weight: 600;
  }
  
  
  .raw-g703-table .row-number {
    background-color: #f5f5f5;
    padding: 4px 6px;
    border: 1px solid #ddd;
    font-weight: 600;
    width: 40px;
    text-align: center;
    position: sticky;
    left: 0;
    z-index: 2;
    font-size: 10px;
  }
  
  .raw-g703-table .table-cell {
    padding: 4px 6px;
    border: 1px solid #ddd;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 150px;
    font-size: 10px;
  }
  
  .raw-g703-table .table-cell.clickable-cell {
    cursor: pointer;
    transition: background-color 0.2s;
  }
  
  .raw-g703-table .table-cell.clickable-cell:hover {
    background-color: #f0f8ff;
  }
  
  /* Column highlighting - Subtle, pleasant colors */
  .raw-g703-table .table-cell.highlight-description {
    background-color: #e3f2fd !important;
    color: #1565c0 !important;
    border: 2px solid #2196f3 !important;
    font-weight: 600;
  }
  
  .raw-g703-table .table-cell.highlight-scheduled_value {
    background-color: #e8f5e8 !important;
    color: #2e7d32 !important;
    border: 2px solid #4caf50 !important;
    font-weight: 600;
  }
  
  .raw-g703-table .table-cell.highlight-work_completed_this_period {
    background-color: #f3e5f5 !important;
    color: #6a1b9a !important;
    border: 2px solid #9c27b0 !important;
    font-weight: 600;
  }
  
  .raw-g703-table .table-cell.highlight-total_completed {
    background-color: #fff3e0 !important;
    color: #e65100 !important;
    border: 2px solid #ff9800 !important;
    font-weight: 600;
  }
  
  .raw-g703-table .table-cell.highlight-work_completed_previous {
    background-color: #fff8e1 !important;
    color: #f57c00 !important;
    border: 2px solid #ffa726 !important;
    font-weight: 600;
  }
  
  .raw-g703-table .table-cell.highlight-materials_stored {
    background-color: #efebe9 !important;
    color: #5d4037 !important;
    border: 2px solid #795548 !important;
    font-weight: 600;
  }
  
  .raw-g703-table .table-cell.highlight-retainage_percent {
    background-color: #ffebee !important;
    color: #c62828 !important;
    border: 2px solid #f44336 !important;
    font-weight: 600;
  }
  
  .raw-g703-table .table-cell.highlight-trade_code {
    background-color: #ede7f6 !important;
    color: #4527a0 !important;
    border: 2px solid #673ab7 !important;
    font-weight: 600;
  }
  
  .raw-g703-table .table-cell.highlight-contractor_names {
    background-color: #e0f2f1 !important;
    color: #00695c !important;
    border: 2px solid #009688 !important;
    font-weight: 600;
  }
  
  /* Additional G703 field highlights - Subtle colors */
  .raw-g703-table .table-cell.highlight-budget_transfer {
    background-color: #f3e5f5 !important;
    color: #6a1b9a !important;
    border: 2px solid #9c27b0 !important;
    font-weight: 600;
  }
  
  .raw-g703-table .table-cell.highlight-owner_previous_approved {
    background-color: #e8eaf6 !important;
    color: #283593 !important;
    border: 2px solid #3f51b5 !important;
    font-weight: 600;
  }
  
  .raw-g703-table .table-cell.highlight-owner_approved_completed {
    background-color: #e3f2fd !important;
    color: #1565c0 !important;
    border: 2px solid #1976d2 !important;
    font-weight: 600;
  }
  
  .raw-g703-table .table-cell.highlight-adjusted_schedule_values {
    background-color: #e0f7fa !important;
    color: #00838f !important;
    border: 2px solid #00bcd4 !important;
    font-weight: 600;
  }
  
  .raw-g703-table .table-cell.highlight-subcontractor_completed {
    background-color: #fff3e0 !important;
    color: #e65100 !important;
    border: 2px solid #ff9800 !important;
    font-weight: 600;
  }
  
  .raw-g703-table .table-cell.highlight-percent_complete {
    background-color: #fbe9e7 !important;
    color: #d84315 !important;
    border: 2px solid #ff5722 !important;
    font-weight: 600;
  }
  
  .raw-g703-table .table-cell.highlight-balance_to_finish {
    background-color: #efebe9 !important;
    color: #5d4037 !important;
    border: 2px solid #795548 !important;
    font-weight: 600;
  }
  
  .raw-g703-table .table-cell.highlight-retainage_this_period {
    background-color: #f9fbe7 !important;
    color: #689f38 !important;
    border: 2px solid #8bc34a !important;
    font-weight: 600;
  }
  
  .raw-g703-table .table-cell.highlight-retainage_total {
    background-color: #fffde7 !important;
    color: #f57f17 !important;
    border: 2px solid #ffeb3b !important;
    font-weight: 600;
  }
  
  /* Subtle highlighting for other mapped columns */
  .raw-g703-table .table-cell.highlight-mapped-other {
    background-color: #e0e0e0 !important;
    color: #666 !important;
    font-weight: 500;
    border: 1px solid #bbb !important;
  }
  
  .extraction-explanation {
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    padding: 15px;
    margin-top: 15px;
  }
  
  .extraction-explanation h6 {
    margin: 0 0 10px 0;
    color: #495057;
    font-size: 14px;
  }
  
  .extraction-explanation p {
    margin: 0 0 8px 0;
    color: #6c757d;
    font-size: 12px;
    line-height: 1.4;
  }
  
  .extraction-explanation p:last-child {
    margin-bottom: 0;
  }
  
  .extraction-explanation p.tip {
    background: #e3f2fd;
    border: 1px solid #90caf9;
    border-radius: 4px;
    padding: 8px;
    color: #1565c0;
    margin-top: 10px;
  }
  
  .extraction-explanation strong {
    color: #343a40;
  }
}

.review-table {
  width: 100%;
  border-collapse: collapse;
  margin: 20px 0;
}

.review-table th,
.review-table td {
  border: 1px solid #ddd;
  padding: 12px;
  text-align: left;
}

.review-table th {
  background: #f5f5f5;
  font-weight: 600;
}

.review-actions {
  display: flex;
  gap: 12px;
  margin-top: 20px;
}

.current-step {
  background: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
}

.mapping-controls {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
}

.dda-mapping-table-container {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
}

.dda-mapping-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.dda-mapping-table th {
  background: #fafafa;
  padding: 12px 8px;
  border-bottom: 2px solid #d9d9d9;
  position: sticky;
  top: 0;
  z-index: 10;
}

.dda-mapping-table td {
  padding: 8px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: top;
}

.selected-row {
  background: #f6ffed !important;
}

.dda-term-wrapper {
  position: relative;
}

.auto-match-badge {
  background: #52c41a;
  color: white;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 10px;
  position: absolute;
  top: -8px;
  right: -8px;
  z-index: 5;
}

.dda-autocomplete {
  position: relative;
}

.dda-search-input {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 12px;
}

.dda-search-input.has-selection {
  background: #f6ffed;
  border-color: #52c41a;
}

.dda-search-input.auto-matched {
  background: #e6fffb;
  border-color: #13c2c2;
}

.dda-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d9d9d9;
  border-top: none;
  border-radius: 0 0 4px 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
}

.dda-option {
  padding: 8px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
}

.dda-option:hover {
  background: #f5f5f5;
}

.term-info {
  display: flex;
  flex-direction: column;
}

.term-name {
  font-weight: 500;
  font-size: 13px;
}

.term-category {
  font-size: 11px;
  color: #666;
}

.pattern-mapping-section {
  margin-top: 30px;
  padding: 20px;
  background: #fafafa;
  border-radius: 8px;
}

.pattern-list {
  margin: 16px 0;
}

.pattern-item {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.pattern-input {
  flex: 1;
  padding: 8px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
}

.pattern-arrow {
  font-weight: bold;
  color: #666;
}

.pattern-select {
  flex: 2;
  padding: 8px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
}

.btn-remove {
  background: #ff4d4f;
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  cursor: pointer;
}

.test-results {
  margin-top: 20px;
  padding: 16px;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 6px;
}

.test-stats {
  display: flex;
  gap: 20px;
  margin-bottom: 12px;
}

.stat-item {
  font-size: 14px;
}

.assignment-rate {
  font-size: 16px;
  font-weight: 600;
  color: #52c41a;
}

.step-navigation {
  display: flex;
  justify-content: center;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #d9d9d9;
}

.btn-primary {
  background: #1890ff;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.btn-primary:disabled {
  background: #d9d9d9;
  cursor: not-allowed;
}

.btn-secondary {
  background: #f5f5f5;
  color: #333;
  border: 1px solid #d9d9d9;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.g703-preview-fallback {
  text-align: center;
  padding: 40px 20px;
  background: #fafafa;
  border-radius: 8px;
}

.placeholder-message {
  margin-bottom: 20px;
}

.actions {
  display: flex;
  justify-content: center;
  gap: 12px;
}

.code-cell, .description-cell, .contractor-cell, .amount-cell {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.description-cell {
  max-width: 200px;
}

.amount-cell {
  text-align: right;
  font-family: monospace;
}
</style> 